import { Injectable, signal, computed, inject } from '@angular/core';
import { Token } from '../models/token.model';

const ACCESS_TOKEN_KEY = 'access_token';
const REFRESH_TOKEN_KEY = 'refresh_token';
// Legacy keys for backward compatibility
const EXPIRES_IN = 'expires_in';
const TOKEN_TYPE = 'token_type';

@Injectable({ providedIn: 'root' })
export class TokenService {
  private _token = signal<Token | null>(this._loadToken());

  constructor() {}

  private _loadToken(): Token | null {
    const accessToken = localStorage.getItem(ACCESS_TOKEN_KEY);
    const refreshToken = localStorage.getItem(REFRESH_TOKEN_KEY);
    const expiresIn = localStorage.getItem(EXPIRES_IN);
    const tokenType = localStorage.getItem(TOKEN_TYPE);
    
    if (accessToken && refreshToken) {
      return { 
        accessToken, 
        refreshToken, 
        // Include legacy fields if they exist for backward compatibility
        expiresIn: expiresIn || undefined, 
        tokenType: tokenType || undefined 
      };
    }
    return null;
  }

  setToken(token: Token) {
    localStorage.setItem(ACCESS_TOKEN_KEY, token.accessToken);
    localStorage.setItem(REFRESH_TOKEN_KEY, token.refreshToken);
    
    // Store legacy fields if provided for backward compatibility
    if (token.expiresIn) {
      localStorage.setItem(EXPIRES_IN, token.expiresIn);
    }
    if (token.tokenType) {
      localStorage.setItem(TOKEN_TYPE, token.tokenType);
    }
    
    this._token.set(token);
  }

  /**
   * Set token from AuthResponse structure (new API format)
   */
  setTokenFromAuthResponse(authResponse: { accessToken: string; refreshToken: string }) {
    const token: Token = {
      accessToken: authResponse.accessToken,
      refreshToken: authResponse.refreshToken
    };
    this.setToken(token);
  }

  getToken(): Token | null {
    return this._token();
  }

  clearToken() {
    localStorage.removeItem(ACCESS_TOKEN_KEY);
    localStorage.removeItem(REFRESH_TOKEN_KEY);
    localStorage.removeItem(EXPIRES_IN);
    localStorage.removeItem(TOKEN_TYPE);
    this._token.set(null);
  }

  accessToken() {
    return computed(() => this._token()?.accessToken ?? null);
  }

  refreshToken() {
    return computed(() => this._token()?.refreshToken ?? null);
  }
  tokenType() {
    return computed(() => this._token()?.tokenType ?? null);
  }

  isAuthenticated() {
    return computed(() => !!this._token()?.accessToken);
  }
}
